import { useMemo } from 'react';

import type { VarationCallback } from 'src/modules/homeownerApp/components/Palette/data';
import { mapVariations } from 'src/modules/homeownerApp/components/Palette/data';
import { useMultipleCategoryProductCatalogProducts } from 'src/modules/homeownerApp/components/Palette/hooks/useMultipleCategoryProductCatalogProducts';
import type { CustomizationCategory } from 'src/modules/homeownerApp/constants/customizationCategories';
import type { ProductCatalogCustomizeCategoriesQuery } from 'src/modules/homeownerApp/graphql/generated';
import { warn } from 'src/modules/homeownerApp/helpers/logging';
import {
  usePartner,
  usePartnerCustomizeCategories,
} from 'src/modules/homeownerApp/hooks/usePartner';
import type {
  Aspect,
  DesignerProductCatalogProduct,
} from 'src/modules/homeownerApp/types';

interface UseProductCatalogProductsOptions<T> {
  aspect: Aspect;
  category: CustomizationCategory | undefined;
  transformer: VarationCallback<T | null>;
  skip?: boolean;
}

const useProductCatalogCategoryIds = (
  productCatalogCategories:
    | ProductCatalogCustomizeCategoriesQuery['genAiCategories']['nodes']
    | undefined,
  aspect: Aspect,
  category: CustomizationCategory | undefined,
) =>
  useMemo(() => {
    if (!productCatalogCategories) return [];

    return productCatalogCategories
      .filter(productCatalogCategory => {
        if (category?.type === 'paint') {
          return (
            (productCatalogCategory?.aiPromptSpaceType === 'any' ||
              productCatalogCategory?.aiPromptSpaceType === aspect) &&
            productCatalogCategory.aiPromptRegion === 'paint'
          );
        }
        return (
          (productCatalogCategory?.aiPromptSpaceType === 'any' ||
            productCatalogCategory?.aiPromptSpaceType === aspect) &&
          productCatalogCategory.aiPromptRegion === category?.region
        );
      })
      .map(
        productCatalogCategory =>
          productCatalogCategory?.productCatalogCategory.id,
      )
      .filter(id => !!id) as string[];
  }, [aspect, category?.type, category?.region, productCatalogCategories]);

export const useProductCatalogProducts = <
  T extends DesignerProductCatalogProduct,
>({
  aspect,
  category,
  transformer,
  skip,
}: UseProductCatalogProductsOptions<T>) => {
  const { partner, isLoading: isLoadingPartner } = usePartner();
  const {
    data: productCatalogCategories,
    loading: productCatalogCategoriesLoading,
  } = usePartnerCustomizeCategories();

  const productCatalogCategoryIds = useProductCatalogCategoryIds(
    productCatalogCategories,
    aspect,
    category,
  );

  const { products: data, loading } = useMultipleCategoryProductCatalogProducts(
    {
      productCatalogCategoryIds,
      skip: skip || productCatalogCategoriesLoading,
    },
  );

  const products = useMemo(
    () => mapVariations(data, transformer),
    [data, transformer],
  );

  if (isLoadingPartner) {
    return [products, { loading: true }] as const;
  }

  if (!isLoadingPartner && !partner) {
    warn('useProductCatalogProducts: missing partner, unable to load products');
  }

  return [products, { loading }] as const;
};
